import type { ElementSettingMap, SlideSettingMap, TargetRef } from "../SettingsPayload";

import { parseTargetRef } from "./targetRef";
import { resolveElementName } from "./resolveElementName";

/**
 * 名前を引くのに要る紙面の情報。走査結果（`ReconciledSlide[]`）をそのまま渡せる形にしてある。
 * `features/office` の型を参照しないのは、`entities` から上位の層を知らないためである。
 */
export type DeckForNames = ReadonlyArray<{
    uid: string;
    index: number;
    shapes: ReadonlyArray<{ uid: string; name: string; creationId?: string | null }>;
}>;

/**
 * 対象の参照（`slide:<識別子>` / `element:<識別子>`）から表示名を引く関数を作る。
 *
 * **枚と枠を同じ表で引く。** 以前は画面ごとに対応表を組んでいて、生成結果ペインの表は
 * 図形しか持っていなかった。そのため `slide:` 宛ての出力が生の参照で並び、実物の
 * フォーマットは 9 本すべてが `slide:` 宛ての対象を 4 件持つので、1 タスクあたり 4 行が
 * 識別子のまま出ていた（記録 69）。画面C の対象一覧も、名前を付けていない枚では識別子が
 * そのまま出ていた。落とし先を `スライド {番号}` に揃えるとメンションの候補や本文の
 * 見出しと呼び名が一致する。
 *
 * 引き当ての順は 3 段である。**設定の名前を先に見る**のは、走査に失敗しても名前が読める
 * ようにするためで（記録 54）、枠の名前は設定が正本だからである。次に走査結果から引き、
 * 最後は識別子そのものへ落とす。走査結果の側でも設定の名前が優先される
 * （{@link resolveElementName}）。
 *
 * 旧鍵（`a16:creationId`）でも引けるようにしてある。台帳に載る出力先は v6・v7 の
 * フォーマットでは図形の creationId を指すため、アドインの識別子だけを鍵にすると名前が
 * 1 件も引けない（記録 15）。
 *
 * 参照ごとに全枚を走らないよう、対応表は 1 度だけ畳む。出力は実物のフォーマットで
 * 350 件規模になるため、参照ごとの走査では枚数と図形数の積になる。
 */
export function createTargetNameResolver(source: {
    deck: DeckForNames;
    slides: SlideSettingMap;
    elements: ElementSettingMap;
}): (ref: TargetRef) => string {
    const { deck, slides, elements } = source;
    const fromDeck = new Map<string, string>();
    for (const slide of deck) {
        const own = slides[slide.uid]?.name?.trim();
        fromDeck.set(slide.uid, own && own.length > 0 ? own : `スライド ${slide.index}`);
        for (const shape of slide.shapes) {
            const name = resolveElementName(elements, shape.uid, shape.name).trim();
            if (name.length === 0) continue;
            fromDeck.set(shape.uid, name);
            // 旧鍵は先に現れた図形へ寄せる。重複した creationId は実物で 68 種あり、
            // 走査も先頭を正とするので向きを揃える（deckStore の `locate` と同じ）。
            if (shape.creationId) {
                const key = normalizeGuid(shape.creationId);
                if (!fromDeck.has(key)) fromDeck.set(key, name);
            }
        }
    }

    return (ref: TargetRef): string => {
        const { kind, officeId } = parseTargetRef(ref);
        const own =
            kind === "slide" ? slides[officeId]?.name?.trim() : elements[officeId]?.name?.trim();
        if (own !== undefined && own.length > 0) return own;
        return fromDeck.get(officeId) ?? fromDeck.get(normalizeGuid(officeId)) ?? officeId;
    };
}

/**
 * GUID の表記ゆれ（波括弧・大文字小文字）を吸収する。
 * `features/office/deckStore` にも同じ処理があるが、層をまたいで持ち出さない。
 */
function normalizeGuid(value: string): string {
    return value
        .trim()
        .replace(/^\{|\}$/g, "")
        .toLowerCase();
}
